import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';

import { BreadcrumbHeader } from '../../components/shared';
import ContentCard from '../../components/shared/ContentCard';
import { purchaseOrders } from '../../services/dataClient';
import '../../styles/design-tokens.css';

import PurchaseOrdersTab from './tabs/PurchaseOrdersTab';

const fmt = (n) =>
new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

function computeRange(preset, customStart, customEnd) {
  const today = dayjs();
  switch (preset) {
    case 'thisMonth':return { start: today.startOf('month').format('YYYY-MM-DD'), end: today.endOf('month').format('YYYY-MM-DD') };
    case 'lastMonth':{
        const lm = today.subtract(1, 'month');
        return { start: lm.startOf('month').format('YYYY-MM-DD'), end: lm.endOf('month').format('YYYY-MM-DD') };
      }
    case 'last3':return { start: today.subtract(3, 'month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
    case 'last6':return { start: today.subtract(6, 'month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
    case 'ytd':return { start: today.startOf('year').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
    case 'custom':
      return {
        start: customStart || today.startOf('month').format('YYYY-MM-DD'),
        end: customEnd || today.format('YYYY-MM-DD')
      };
    default:return { start: today.startOf('month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
  }
}

const inRange = (dateStr, start, end) =>
Boolean(dateStr) && dateStr >= start && dateStr <= end;

// ─── PO summary bar ───────────────────────────────────────────────────────────
function SummaryBar({ pos }) {
  const total = pos.reduce((s, p) => s + p.totalCost, 0);
  const pending = pos.filter((p) => p.status === 'Pending').length;
  const received = pos.filter((p) => p.status === 'Received').length;
  const ordered = pos.filter((p) => p.status === 'Ordered').length;

  const stats = [
  { label: 'Total PO Value', value: fmt(total) },
  { label: 'Pending', value: pending },
  { label: 'Ordered', value: ordered },
  { label: 'Received', value: received }];

  return (
    <ContentCard
      sx={{ display: 'flex',

        overflow: 'hidden'
      }}>
      
      {stats.map((s, i) =>
      <Box
        key={s.label}
        sx={{ flex: 1,
          px: 3,
          py: 2,
          borderRight: i < stats.length - 1 ? '1px solid var(--grey-200)' : 'none'
        }}>
        
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--grey-900)', lineHeight: 1.2 }}>
            {s.value}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '4px' }}>
            {s.label}
          </Typography>
        </Box>
      )}
    </ContentCard>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PurchaseOrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const preset = searchParams.get('preset') ?? 'thisMonth';
  const customStart = searchParams.get('customStart') ?? '';
  const customEnd = searchParams.get('customEnd') ?? '';

  const setParam = (key, value) =>
  setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value) next.set(key, value);else next.delete(key);
    return next;
  }, { replace: true });

  const setPreset = (v) => setParam('preset', v === 'thisMonth' ? '' : v);
  const setCustomStart = (v) => setParam('customStart', v);
  const setCustomEnd = (v) => setParam('customEnd', v);

  // Mutable local state for create/edit operations (resets on refresh)
  const [pos, setPos] = useState(purchaseOrders);

  const { start: startDate, end: endDate } = useMemo(
    () => computeRange(preset, customStart, customEnd),
    [preset, customStart, customEnd]
  );

  const filteredPos = useMemo(
    () => pos.filter((p) => inRange(p.date, startDate, endDate)),
    [pos, startDate, endDate]
  );

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <BreadcrumbHeader
        title="Purchase Orders"
        crumbs={[{ label: 'Accounting' }, { label: 'Purchase Orders' }]} />

      {/* PO summary */}
      <SummaryBar pos={filteredPos} />

      {/* Content card */}
      <ContentCard
        sx={{ p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
        
        <PurchaseOrdersTab
          pos={filteredPos}
          allPos={pos}
          onUpdate={setPos}
          preset={preset}
          onPresetChange={setPreset}
          customStart={customStart}
          onCustomStartChange={setCustomStart}
          customEnd={customEnd}
          onCustomEndChange={setCustomEnd} />
        
      </ContentCard>
    </Box>);

}